import React, { useEffect, useRef, useState } from "react";
import "./AddSupervisorModal.scss";

import closeIcon from "../../../../../../Assets/images/close-icon.svg";

import apiClient from "../../../../../../Config/apiClient";
import { isValidEmail } from "../../../../../../Utils/Validations/validateEmail";
import { isValidPassword } from "../../../../../../Utils/Validations/validatePassword";
import { isValidCanadianPhone } from "../../../../../../Utils/Validations/validatePhone";
import { FIELD_MAX_LENGTHS } from "../../../Utils/Constants";
import PasswordTooltip from "../../../../../../Utils/PasswordToolTip/PasswordToolTip";
import Loader from "../../../../../../Context/Loader/Loader";
import { showErrorToast, showSuccessToast } from "../../../../../../Context/Toaster/Toaster";

const rolesPath = process.env.REACT_APP_CC_ROLES_BASE_PATH;
const supervisorsPath = process.env.REACT_APP_CC_SUPERVISORS_BASE_PATH;

const AddSupervisorsModal = ({ refreshSupervisorList }) => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    contact_no: "",
    email_id: "",
    password: "",
    role_id: "",
  });

  const [roles, setRoles] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const passwordRef = useRef(null);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await apiClient.get(`${rolesPath}/list`);
        if (response.data.status === "SUCCESS") {
          setRoles(response.data.data);
        }
      } catch (err) {
        showErrorToast("Failed to fetch permissions");
      }
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (passwordRef.current && !passwordRef.current.contains(event.target)) {
        setIsPasswordFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateFields = () => {
    const newErrors = {};
    const { first_name, last_name, contact_no, email_id, password, role_id } = formData;

    if (!first_name.trim()) newErrors.first_name = "First name is required.";
    if (!last_name.trim()) newErrors.last_name = "Last name is required.";

    if (!contact_no.trim()) {
      newErrors.contact_no = "Contact number is required.";
    } else if (!isValidCanadianPhone(contact_no)) {
      newErrors.contact_no = "Invalid contact number.";
    }

    if (!email_id.trim()) {
      newErrors.email_id = "Email is required.";
    } else if (!isValidEmail(email_id)) {
      newErrors.email_id = "Invalid email address.";
    }

    const pwdCheck = isValidPassword(password);
    if (!pwdCheck.valid) {
      newErrors.password = pwdCheck.message;
    }

    if (!role_id) newErrors.role_id = "Permission Name is required.";;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;

    setLoading(true);
    try {
      const response = await apiClient.post(`${supervisorsPath}/add`, formData);
      if (response.data.status === "SUCCESS") {
        showSuccessToast("Supervisor added successfully.");
        refreshSupervisorList();
        setFormData({
          first_name: "",
          last_name: "",
          contact_no: "",
          email_id: "",
          password: "",
          role_id: "",
        });
        setErrors({});
        document.getElementById("addSupervisorModalClose")?.click();
      } else {
        showErrorToast(response.data.message || "Failed to add Supervisor.");
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      showErrorToast(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal fade" id="addSupervisorModal" tabIndex="-1" aria-labelledby="addSupervisorModalLabel" aria-hidden="true" data-bs-backdrop="static"data-bs-keyboard="false">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <form onSubmit={handleSubmit} noValidate>
            <div className="cprofile-content">
              <div className="cpf-top">
                <h2 id="addSupervisorModalLabel">Add Supervisor</h2>
                <button id="addSupervisorModalClose" type="button" className="m-close" data-bs-dismiss="modal" aria-label="Close">
                  <img src={closeIcon} alt="Close" />
                </button>
              </div>

              <div className="cprofile-inner">
                <div className="cp-form">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">First Name<span>*</span></label>
                        <input type="text" name="first_name" maxLength={FIELD_MAX_LENGTHS.firstName} className={`common-input ${errors.first_name ? "error" : ""}`} value={formData.first_name} onChange={handleChange} />
                        <span className={`error-text ${errors.first_name ? "active" : ""}`}>{errors.first_name}</span>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">Last Name<span>*</span></label>
                        <input type="text" name="last_name" maxLength={FIELD_MAX_LENGTHS.lastName} className={`common-input ${errors.last_name ? "error" : ""}`} value={formData.last_name} onChange={handleChange} />
                        <span className={`error-text ${errors.last_name ? "active" : ""}`}>{errors.last_name}</span>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">Contact No.<span>*</span></label>
                        <input type="tel" name="contact_no" maxLength={FIELD_MAX_LENGTHS.contact} className={`common-input ${errors.contact_no ? "error" : ""}`} value={formData.contact_no} onChange={handleChange} />
                        <span className={`error-text ${errors.contact_no ? "active" : ""}`}>{errors.contact_no}</span>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">Permission Name<span>*</span></label>
                        <select name="role_id" className={`common-select ${errors.role_id ? "error" : ""}`} value={formData.role_id} onChange={handleChange}>
                          <option value="">--Select--</option>
                          {roles.map(role => (
                            <option key={role.id} value={role.id}>{role.role_name}</option>
                          ))}
                        </select>
                        <span className={`error-text ${errors.role_id ? "active" : ""}`}>{errors.role_id}</span>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">Email<span>*</span></label>
                        <input type="email" name="email_id" maxLength={FIELD_MAX_LENGTHS.email} className={`common-input ${errors.email_id ? "error" : ""}`} value={formData.email_id} onChange={handleChange} />
                        <span className={`error-text ${errors.email_id ? "active" : ""}`}>{errors.email_id}</span>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className={`cp-split password-box pass-input ${isPasswordFocused ? "focused" : ""}`} ref={passwordRef} style={{ position: "relative" }} >
                        <label className="fi-label">
                          Password<span>*</span>
                        </label>

                        {isPasswordFocused && formData.password === "" && (
                          <div style={{ position: "absolute", top: "45%", left: "0", zIndex: 2 }} >
                            <PasswordTooltip />
                          </div>
                        )}
                        <input type={showPassword ? "text" : "password"} name="password" maxLength={FIELD_MAX_LENGTHS.password} className={`common-input ${errors.password ? "error" : ""}`} value={formData.password} onFocus={() => setIsPasswordFocused(true)} onBlur={() => setIsPasswordFocused(false)} onChange={handleChange} />
                        <i className={`fa field-icon ${showPassword ? "fa-eye" : "fa-eye-slash"}`} onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", top: "70%", right: "10px", transform: "translateY(-50%)", cursor: "pointer" }} />
                        <span className={`error-text ${errors.password ? "active" : ""}`}>
                          {errors.password}
                        </span>
                      </div>
                    </div>

                  </div>

                  <div className="m-button">
                    <button type="submit" className="cp-button save-btn" disabled={loading}>
                      {loading ? <Loader /> : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddSupervisorsModal;
